import React, { useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdSectionCard,
} from "../../ipd/utils";
import { recordDeathCause } from "../api/emergencyApi";

/**
 * MCCD Form 4 cause of death (emergency-death-registration, ticket 02).
 *
 * The statutory structured cause — Part I is the causal chain (immediate a →
 * antecedent b → underlying c), Part II other significant conditions — plus the
 * manner of death. A sudden or brought-in-dead death is registered without a
 * fabricated cause via the "cause pending" path, which carries only a reason.
 *
 * Recording a cause is a deliberate action on the closed (expired) visit, the
 * same exception certification is.
 */

const MANNER = {
  natural: "Natural",
  accident: "Accident",
  suicide: "Suicide",
  homicide: "Homicide",
  pending_investigation: "Pending investigation",
};
const MANNER_OPTIONS = Object.entries(MANNER).map(([value, label]) => ({ value, label }));
const INJURY_MANNERS = new Set(["accident", "suicide", "homicide"]);
const blankLine = () => ({ condition: "", interval: "" });

export default function DeathCauseCard({ visit, onChanged }) {
  const record = visit.death_record;

  const [editing, setEditing] = useState(false);
  const [manner, setManner] = useState("");
  const [pending, setPending] = useState(false);
  const [pendingReason, setPendingReason] = useState("");
  const [part1, setPart1] = useState({ a: blankLine(), b: blankLine(), c: blankLine() });
  const [part2, setPart2] = useState([blankLine()]);
  const [injury, setInjury] = useState("");
  const [pregnancy, setPregnancy] = useState("");
  const [delivery, setDelivery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!record) return null;

  const female = (visit.gender || "").toUpperCase() === "F";

  const openEditor = () => {
    setManner(record.manner_of_death || "");
    setPending(Boolean(record.cause_pending));
    setPendingReason(record.cause_pending_reason || "");
    const byLine = { a: blankLine(), b: blankLine(), c: blankLine() };
    (record.mccd_part1 || []).forEach((r) => {
      if (byLine[r.line]) byLine[r.line] = { condition: r.condition || "", interval: r.interval || "" };
    });
    setPart1(byLine);
    const p2 = (record.mccd_part2 || []).map((r) => ({
      condition: r.condition || "",
      interval: r.interval || "",
    }));
    setPart2(p2.length ? p2 : [blankLine()]);
    setInjury(record.mccd_injury_description || "");
    setPregnancy(record.mccd_pregnancy_associated == null ? "" : String(record.mccd_pregnancy_associated));
    setDelivery(record.mccd_delivery_occurred == null ? "" : String(record.mccd_delivery_occurred));
    setError("");
    setEditing(true);
  };

  const setLine = (line, patch) => setPart1((p) => ({ ...p, [line]: { ...p[line], ...patch } }));
  const setPart2Row = (i, patch) =>
    setPart2((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addPart2Row = () => setPart2((rows) => [...rows, blankLine()]);
  const removePart2Row = (i) =>
    setPart2((rows) => (rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows));

  const canSave = pending ? Boolean(pendingReason.trim()) : Boolean(part1.a.condition.trim());

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    try {
      const built = pending
        ? {}
        : {
            part1: ["a", "b", "c"]
              .map((line) => ({ line, condition: part1[line].condition.trim(), interval: part1[line].interval.trim() }))
              .filter((r) => r.condition),
            part2: part2
              .map((r) => ({ condition: r.condition.trim(), interval: r.interval.trim() }))
              .filter((r) => r.condition),
          };
      await recordDeathCause(visit.id, {
        manner: manner || undefined,
        causePending: pending,
        causePendingReason: pending ? pendingReason.trim() : undefined,
        injuryDescription: injury.trim() || undefined,
        pregnancyAssociated: pregnancy === "" ? undefined : pregnancy === "true",
        deliveryOccurred: delivery === "" ? undefined : delivery === "true",
        ...built,
      });
      await onChanged?.();
      setEditing(false);
    } catch (e) {
      setError(e?.message || "Could not record the cause of death");
    } finally {
      setSaving(false);
    }
  };

  const hasCause = record.cause_pending || record.cause_of_death || record.manner_of_death;

  return (
    <IpdSectionCard
      title="Cause of death"
      subtitle="MCCD Form 4 — the certifiable cause and manner"
      actions={
        !editing ? (
          <IpdButton variant="outlined" size="small" startIcon={<EditIcon />} onClick={openEditor}>
            {hasCause ? "Update" : "Record"}
          </IpdButton>
        ) : null
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {!editing && (
        !hasCause ? (
          <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary }}>
            Cause of death not recorded yet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {record.cause_pending ? (
              <Typography sx={{ fontSize: 13.5, fontStyle: "italic", color: IPD_COLORS.warning.text }}>
                Cause pending — {record.cause_pending_reason}
              </Typography>
            ) : record.cause_of_death ? (
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: IPD_COLORS.textPrimary }}>
                {record.cause_of_death}
              </Typography>
            ) : null}
            {record.manner_of_death && (
              <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
                Manner: {MANNER[record.manner_of_death] || record.manner_of_death}
              </Typography>
            )}
          </Box>
        )
      )}

      <Collapse in={editing} unmountOnExit>
        <Box sx={{ display: "grid", gap: 1.75 }}>
          <Box>
            <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Manner of death</Typography>
            <TextField
              select
              value={manner}
              onChange={(e) => setManner(e.target.value)}
              size="small"
              fullWidth
              sx={{ ...ipdElevatedInputSx, maxWidth: 320 }}
              SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
              inputProps={{ "aria-label": "Manner of death" }}
            >
              <MenuItem value="">Not stated</MenuItem>
              {MANNER_OPTIONS.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </TextField>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={pending}
                onChange={(e) => setPending(e.target.checked)}
                size="small"
                sx={{ color: IPD_COLORS.textMuted, "&.Mui-checked": { color: IPD_COLORS.primary } }}
              />
            }
            label={
              <Typography sx={{ fontSize: 13.5 }}>
                Cause is pending (e.g. awaiting post-mortem) — register without a cause
              </Typography>
            }
          />

          {pending ? (
            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>
                Reason pending <span style={{ color: IPD_COLORS.error.text }}>*</span>
              </Typography>
              <TextField
                value={pendingReason}
                onChange={(e) => setPendingReason(e.target.value)}
                size="small"
                fullWidth
                placeholder="Awaiting post-mortem, cause under investigation…"
                sx={ipdElevatedInputSx}
                inputProps={{ "aria-label": "Reason the cause is pending" }}
              />
            </Box>
          ) : (
            <>
              <Box>
                <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>
                  Part I — cause chain (immediate first)
                </Typography>
                <Box sx={{ display: "grid", gap: 1 }}>
                  {[
                    ["a", "Immediate cause"],
                    ["b", "Due to (antecedent)"],
                    ["c", "Due to (underlying)"],
                  ].map(([line, label]) => (
                    <Box key={line} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 140px" }, gap: 1 }}>
                      <TextField
                        value={part1[line].condition}
                        onChange={(e) => setLine(line, { condition: e.target.value })}
                        size="small"
                        placeholder={label}
                        required={line === "a"}
                        sx={ipdElevatedInputSx}
                        inputProps={{ "aria-label": label }}
                      />
                      <TextField
                        value={part1[line].interval}
                        onChange={(e) => setLine(line, { interval: e.target.value })}
                        size="small"
                        placeholder="Interval"
                        sx={ipdElevatedInputSx}
                        inputProps={{ "aria-label": `${label} interval` }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>
                  Part II — other significant conditions
                </Typography>
                <Box sx={{ display: "grid", gap: 1 }}>
                  {part2.map((row, i) => (
                    <Box
                      key={i}
                      sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 140px auto" }, gap: 1, alignItems: "center" }}
                    >
                      <TextField
                        value={row.condition}
                        onChange={(e) => setPart2Row(i, { condition: e.target.value })}
                        size="small"
                        placeholder="Contributing condition"
                        sx={ipdElevatedInputSx}
                        inputProps={{ "aria-label": `Other significant condition ${i + 1}` }}
                      />
                      <TextField
                        value={row.interval}
                        onChange={(e) => setPart2Row(i, { interval: e.target.value })}
                        size="small"
                        placeholder="Interval"
                        sx={ipdElevatedInputSx}
                        inputProps={{ "aria-label": `Other significant condition ${i + 1} interval` }}
                      />
                      <Box
                        component="button"
                        type="button"
                        aria-label={`Remove other condition ${i + 1}`}
                        disabled={part2.length === 1}
                        onClick={() => removePart2Row(i)}
                        sx={{
                          border: "none",
                          background: "none",
                          cursor: part2.length === 1 ? "default" : "pointer",
                          color: part2.length === 1 ? IPD_COLORS.border : IPD_COLORS.textMuted,
                          p: 0.5,
                          "&:hover": { color: part2.length === 1 ? IPD_COLORS.border : IPD_COLORS.error.text },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </Box>
                    </Box>
                  ))}
                  <Box>
                    <IpdButton variant="outlined" size="small" startIcon={<AddIcon />} onClick={addPart2Row}>
                      Add condition
                    </IpdButton>
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {INJURY_MANNERS.has(manner) && (
            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>How the injury occurred</Typography>
              <TextField
                value={injury}
                onChange={(e) => setInjury(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={2}
                sx={ipdElevatedInputSx}
                inputProps={{ "aria-label": "How the injury occurred" }}
              />
            </Box>
          )}

          {female && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
              <Box>
                <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Pregnant / recently pregnant</Typography>
                <TextField
                  select value={pregnancy} onChange={(e) => setPregnancy(e.target.value)}
                  size="small" fullWidth sx={ipdElevatedInputSx}
                  SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
                  inputProps={{ "aria-label": "Pregnant or recently pregnant" }}
                >
                  <MenuItem value="">Not answered</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Box>
              <Box>
                <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Delivery occurred</Typography>
                <TextField
                  select value={delivery} onChange={(e) => setDelivery(e.target.value)}
                  size="small" fullWidth sx={ipdElevatedInputSx}
                  SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
                  inputProps={{ "aria-label": "Delivery occurred" }}
                >
                  <MenuItem value="">Not answered</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <IpdButton variant="outlined" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </IpdButton>
            <IpdButton onClick={submit} loading={saving} disabled={!canSave}>
              Save cause
            </IpdButton>
          </Box>
        </Box>
      </Collapse>
    </IpdSectionCard>
  );
}
